import React, { FC, useCallback, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { IconTypes } from 'components/Icons';
import { getIcon } from 'components/Icons/Components/getIcon';
import {
  renderSideNavHeader,
  SideNav,
  sideNavCategoryHeaderStyles,
  SideNavKey,
  SideNavMenuItemProps,
} from 'components/Navigation/SideNav';

import { selectSoftwareInventory } from '../selectors';
import {
  getSoftwareCategoryIcon,
  getSoftwareCategoryLabel,
  getSoftwareSubcategories,
  getSoftwareSubcategoryLabel,
  sortSoftwareCategories,
} from '../utils';

import { SoftwareInventoryCategory } from './SoftwareInventoryCategory';
import { SoftwareInventoryOverview } from './SoftwareInventoryOverview';

export const SoftwareInventorySidebar: FC = () => {
  const { summary } = useSelector(selectSoftwareInventory);
  const [current, setCurrent] = useState<SideNavKey>('overview');
  const selectSubcategory = useCallback(
    (category: string, subcategory: string) =>
      setCurrent(`subcategory:${category}:${subcategory}`),
    [],
  );

  const items = useMemo<SideNavMenuItemProps[]>(() => {
    const categoryItems = sortSoftwareCategories(summary.data.categories).map(
      category => ({
        title: getSoftwareCategoryLabel(category.category),
        key: `category:${category.category}`,
        icon: getIcon(getSoftwareCategoryIcon(category.category)),
        component: <SoftwareInventoryCategory category={category} />,
        secondary: getSoftwareSubcategories(category).map(subcategory => ({
          title: getSoftwareSubcategoryLabel(
            category.category,
            subcategory.key,
          ),
          key: `subcategory:${category.category}:${subcategory.key}`,
          component: (
            <SoftwareInventoryCategory
              category={category}
              subcategory={subcategory.key}
            />
          ),
          styles: { menuItem: { pl: 12 } },
        })),
      }),
    );

    return [
      {
        title: renderSideNavHeader('Overview', IconTypes.DashboardOverview),
        key: 'overview',
        data: { name: 'Overview' },
        component: (
          <SoftwareInventoryOverview
            summary={summary.data}
            onSelectSubcategory={selectSubcategory}
          />
        ),
        showDivider: true,
      },
      {
        title: renderSideNavHeader(
          'By Categories',
          IconTypes.DashboardCategory,
        ),
        key: 'by-categories',
        showArrow: false,
        styles: sideNavCategoryHeaderStyles,
        secondary: categoryItems,
      },
    ];
  }, [selectSubcategory, summary.data]);

  return (
    <Box h="full">
      <SideNav
        menuItems={items}
        isLoading={summary.isLoading}
        showContentHeader={false}
        onChange={key => setCurrent(key)}
        current={current}
        leftAlignSecondaryItems
        styles={{
          menu: { index: [1], defaultIndex: [] },
        }}
      />
    </Box>
  );
};
